/**
 * "Find a resident": the search form and its results (PATIENT_RECORD_SEARCH.md sections 2c and 5).
 *
 * One text box, an optional exact date of birth, an explicit Search button. No search-as-you-type: every
 * search is audited and rate limited on the server, so one press is one search. The term goes in a POST body.
 * An SSN-shaped term and a term under 3 characters are refused here, before anything is sent.
 *
 * The list is the minimum necessary: a birth YEAR (never a date of birth), the owner and one stage word per
 * board, and a "DOB matches" badge when the date typed in matched. No export, no copy-all.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import {
  PAGE_SIZE, checkTerm, residentDirectoryApi,
  type DirectoryRow, type SearchPage,
} from './residentDirectoryApi';
import {
  NO_BUILDING, NO_NAME, financeOwnerWords, paOwnerWords, phrase, providerWords, umedOwnerWords,
} from './residentWords';
import './residentDirectory.css';

const MATCHED_ON: Record<string, string> = { BUILDING: 'Matched on building', COUNSELEAR_ID: 'Matched on CounselEAR id', CASE_REF: 'Matched on case reference' };

function Cell({ board, owner, stage }: { board: string; owner: string; stage: string | null }) {
  return (
    <span className="rd-cell">
      <span className="rd-cell-k">{board}</span>
      <span className="rd-cell-v">{owner}</span>
      {stage ? <span className="rd-cell-s">{phrase(stage)}</span> : null}
    </span>
  );
}

function Row({ row, onOpen }: { row: DirectoryRow; onOpen: (id: string, trigger: HTMLElement) => void }) {
  const clinic = row.clinic?.name || null;
  const where = `${row.building?.name || NO_BUILDING}${clinic ? ` · ${clinic}` : ''}`;
  return (
    <li>
      <button type="button" className="rd-row" aria-haspopup="dialog" onClick={(e) => onOpen(row.patient_id, e.currentTarget)}>
        <span className="rd-who">
          {row.name ? <strong className="rd-name">{row.name}</strong> : <em className="rd-name rd-noname">{NO_NAME}</em>}
          {row.birth_year ? <span className="rd-meta">Born {row.birth_year}</span> : null}
          {row.counselear_id ? <span className="rd-meta">CounselEAR {row.counselear_id}</span> : null}
          {row.dob_match ? <span className="rd-badge">DOB matches</span> : null}
        </span>
        <span className="rd-where">
          <span>{where}</span>
          {row.resident_status ? <span className="rd-meta">{phrase(row.resident_status)}</span> : null}
          {MATCHED_ON[row.matched_on] ? <span className="rd-meta">{MATCHED_ON[row.matched_on]}</span> : null}
        </span>
        <span className="rd-boards">
          <Cell board="PA" owner={paOwnerWords(row.owners.pa)} stage={row.stages.pa} />
          <Cell board="UMED" owner={umedOwnerWords(row.owners)} stage={row.stages.umed} />
          <Cell board="Finance" owner={financeOwnerWords(row.owners.finance)} stage={row.stages.finance} />
          <Cell board="Providers" owner={providerWords(row.owners)} stage={null} />
        </span>
      </button>
    </li>
  );
}

export interface ResidentDirectoryProps {
  /** A row was chosen (click, Enter or Space). `trigger` is the row, so focus can return to it. */
  onOpen: (patientId: string, trigger: HTMLElement) => void;
}

export default function ResidentDirectory({ onOpen }: ResidentDirectoryProps) {
  const qId = useId();
  const dobId = useId();
  const [q, setQ] = useState('');
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [page, setPage] = useState<SearchPage | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  // What the shown results are for. Paging repeats it, not whatever is typed in the boxes now.
  const asked = useRef<{ q: string; dob: string } | null>(null);
  const seq = useRef(0);
  useEffect(() => () => { seq.current += 1; }, []);

  async function run(term: string, born: string, n: number) {
    seq.current += 1;
    const mine = seq.current;
    setBusy(true);
    setProblem(null);
    const r = await residentDirectoryApi.search(term, born || null, n);
    if (mine !== seq.current) return;
    setBusy(false);
    if (r.ok && r.data) { setPage(r.data); asked.current = { q: term, dob: born }; return; }
    setPage(null);
    setProblem(r.message || 'Resident search could not be completed. Try again.');
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    const term = q.trim();
    const refusal = checkTerm(term);
    if (refusal) { seq.current += 1; setPage(null); setProblem(refusal); return; }
    run(term, dob, 1);
  }

  const go = (n: number) => { if (asked.current && !busy) run(asked.current.q, asked.current.dob, n); };

  const limit = page?.limit || PAGE_SIZE;
  const pages = page ? Math.max(1, Math.ceil(page.total / limit)) : 1;
  let status = '';
  if (busy) status = 'Searching…';
  else if (problem) status = problem;
  else if (page) {
    status = page.total === 0 ? 'No resident found.' : `${page.total} ${page.total === 1 ? 'resident' : 'residents'} found.`;
  }

  return (
    <div className="rd-search">
      <form className="rd-form" onSubmit={submit} noValidate>
        <div className="rd-field">
          <label htmlFor={qId}>Name, CounselEAR id, case reference or building</label>
          <input id={qId} type="text" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" spellCheck={false} />
        </div>
        <div className="rd-field rd-field-dob">
          <label htmlFor={dobId}>Date of birth (exact, optional)</label>
          <input id={dobId} type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </div>
        <button type="submit" className="rd-go" disabled={busy}>Search</button>
      </form>

      <p className={`rd-status${problem ? ' rd-status-problem' : ''}`} aria-live="polite">{status}</p>

      {page && page.items.length > 0 && (
        <>
          <ul className="rd-rows" aria-label="Residents found">
            {page.items.map((row) => <Row key={row.patient_id} row={row} onOpen={onOpen} />)}
          </ul>
          <nav className="rd-pager" aria-label="Result pages">
            <button type="button" className="rd-ghost" onClick={() => go(page.page - 1)} disabled={busy || page.page <= 1}>Previous</button>
            <span className="rd-pageof">Page {page.page} of {pages} · {page.total} in all</span>
            <button type="button" className="rd-ghost" onClick={() => go(page.page + 1)} disabled={busy || page.page >= pages}>Next</button>
          </nav>
        </>
      )}
    </div>
  );
}
